import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSpinner,
  IonCard,
  IonCardContent,
  IonGrid,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonCheckbox,
} from '@ionic/react';
import {
  chevronBack, send, chevronDownOutline, documentTextOutline, mailOutline, searchOutline, closeOutline,
} from 'ionicons/icons';
import {
  getEmailContent, sendEntityEmail,
  type ZohoEmailEntityType, type ZohoEmailTemplateOption, type ZohoEmailFromAddress, type ZohoEmailContact,
} from '../services/zohoInvoicingService';
import styles from './EmailCompose.module.scss';
import BasePageWithHeader from '../components/BasePageWithHeader';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// Strips every form of executable content before the HTML goes into the
// preview iframe — <script> blocks, inline on*="…" event handler attributes
// (onclick/onload/onerror/etc., which trigger the exact same "blocked script
// execution" warning as a <script> tag), and javascript: URLs. The iframe is
// intentionally sandboxed WITHOUT allow-scripts (allow-scripts +
// allow-same-origin together lets embedded content reach back into the
// parent page — the browser's own "can escape its sandboxing" warning), so
// this is what actually eliminates the console warning without loosening
// the sandbox: there's nothing executable left for the browser to block.
function stripScripts(html: string): string {
  return html
    // Paired <script>...</script> blocks.
    .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
    // Any remaining <script ...> tag with no matching close (self-closed or
    // truncated) — the paired-block regex above only matches when a real
    // </script> exists.
    .replace(/<script\b[^>]*>/gi, '')
    // Nested iframe/object/embed — each is its own executable context that
    // needs allow-scripts independently of anything above.
    .replace(/<iframe[\s\S]*?<\/iframe\s*>/gi, '')
    .replace(/<object[\s\S]*?<\/object\s*>/gi, '')
    .replace(/<embed\b[^>]*>/gi, '')
    // Inline on*="…" / on*='…' / on*=unquoted event handler attributes.
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    // javascript:/vbscript: URLs in any attribute, not just href.
    .replace(/((?:href|src|action|formaction)\s*=\s*["'])\s*(?:javascript|vbscript):[^"']*(["'])/gi, '$1#$2');
}

// ─── Portal dropdown — same pattern as CreateInvoice.tsx/PriceCalculator.tsx's
// ─── SelectField/PickerDropdown (itself copied from BlockedTimeForm.tsx). ─────
type PickerRect = { top: number; left?: number; width?: number };

function usePickerDropdown(
  isOpen: boolean,
  fieldRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  close: () => void,
) {
  const [rect, setRect] = useState<PickerRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PickerRect | null => {
      const field = fieldRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      return { top: box.bottom - anchor.top + 4, left: box.left - anchor.left, width: box.width };
    };
    const applyRect = (r: PickerRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };
    const handleResize = () => {
      const r = computeRect();
      if (r) { setRect(r); applyRect(r); }
    };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, fieldRef, portalRef, cardRef, close]);

  return rect;
}

function PickerDropdown({
  isOpen, fieldRef, portalRef, close, className, children,
}: {
  isOpen: boolean;
  fieldRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  close: () => void;
  className: string;
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePickerDropdown(isOpen, fieldRef, portalRef, cardRef, close);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);
  if (!isOpen || !rect || !portalNode) return null;
  return createPortal(
    <IonCard ref={cardRef} className={className} style={{ ...rect, position: 'absolute' }}>
      {children}
    </IonCard>,
    portalNode,
  );
}

// Identifies a from_emails entry — org-wide addresses have no per-contact id
// (is_org_email_id: true instead), everything else is keyed by its
// organization_contact_id.
function fromKeyOf(f: ZohoEmailFromAddress): string {
  return f.is_org_email_id ? 'org' : (f.organization_contact_id ?? f.user_name ?? '');
}
function fromLabelOf(f: ZohoEmailFromAddress): string {
  return f.email || f.user_name || (f.is_org_email_id ? i18n.t('pages.EmailCompose.organizationEmail') : i18n.t('pages.EmailCompose.unknownSender'));
}

// ─── From field — real selectable dropdown over Zoho's own from_emails list
// ─── (from getEmailContent), not a hardcoded address. ──────────────────────────
function FromField({
  options, selectedKey, onChange, portalRef,
}: {
  options: ZohoEmailFromAddress[];
  selectedKey: string;
  onChange: (key: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
}) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const selected = options.find(f => fromKeyOf(f) === selectedKey);

  return (
    <>
      <IonItem ref={fieldRef} button detail={false} lines="none" className={styles.selectField} disabled={options.length === 0}
        onClick={() => setOpen(v => !v)}>
        <IonLabel className={!selected ? styles.selectPlaceholder : undefined}>
          {selected ? fromLabelOf(selected) : (options.length ? tr('pages.EmailCompose.selectSender') : tr('pages.EmailCompose.noSenderConfigured'))}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        className={styles.calendarDropdown}>
        <IonList lines="none" className={styles.dropdownList}>
          {options.map(f => {
            const key = fromKeyOf(f);
            return (
              <IonItem key={key} button detail={false} lines="none"
                className={`${styles.dropdownItem}${key === selectedKey ? ` ${styles.dropdownItemActive}` : ''}`}
                onClick={() => { onChange(key); setOpen(false); }}>
                <IonLabel><h3>{fromLabelOf(f)}</h3></IonLabel>
              </IonItem>
            );
          })}
        </IonList>
      </PickerDropdown>
    </>
  );
}

interface RecipientOption {
  key: string;
  name: string;
  email: string;
}

// ─── Recipient field (To/Cc/Bcc) — picks from the contacts Zoho itself
// ─── associates with THIS invoice/order (to_contacts / cc_mails_list), not
// ─── the whole company contact directory, plus lets you type a one-off email
// ─── address directly for a recipient not on that list. ───────────────────────
function RecipientField({
  label, value, onChange, portalRef, options,
}: {
  label: string;
  value: string; // comma-separated emails
  onChange: (v: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
  options: RecipientOption[];
}) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fieldRef = useRef<HTMLIonItemElement>(null);
  const close = useCallback(() => { setOpen(false); setSearch(''); }, []);

  const emails = value.split(',').map(s => s.trim()).filter(Boolean);
  const addEmail = (email: string) => {
    if (!email || emails.includes(email)) return;
    onChange([...emails, email].join(', '));
  };
  const removeEmail = (email: string) => onChange(emails.filter(e => e !== email).join(', '));

  const q = search.trim().toLowerCase();
  const filtered = q
    ? options.filter(o => o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q))
    : options;
  const typedEmail = search.trim();
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typedEmail);

  return (
    <IonLabel className={styles.formGroup}>
      <span className={styles.fieldLabel}>{label}</span>
      <IonItem ref={fieldRef} button detail={false} lines="none" className={styles.selectField} onClick={() => setOpen(v => !v)}>
        <IonLabel className={emails.length === 0 ? styles.selectPlaceholder : undefined}>
          {emails.length > 0 ? `${emails.length} recipient${emails.length !== 1 ? 's' : ''}` : tr('pages.EmailCompose.addRecipient')}
        </IonLabel>
        <IonIcon icon={chevronDownOutline} slot="end"
          className={`${styles.selectCaret} ${open ? styles.selectCaretOpen : ''}`} />
      </IonItem>
      <PickerDropdown isOpen={open} fieldRef={fieldRef} portalRef={portalRef} close={close}
        className={styles.calendarDropdown}>
        <IonItem lines="none" className={styles.searchBox}>
          <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
          <IonInput
            className={styles.searchInput}
            value={search}
            onIonInput={e => setSearch(e.detail.value ?? '')}
            placeholder={options.length ? tr('pages.EmailCompose.searchOrTypeAnEmail') : tr('pages.EmailCompose.typeAnEmailAddress')}
          />
        </IonItem>
        <IonList lines="none" className={styles.dropdownList}>
          {isValidEmail && !emails.includes(typedEmail) && (
            <IonItem button detail={false} lines="none" className={styles.dropdownItem}
              onClick={() => { addEmail(typedEmail); setSearch(''); }}>
              <IonLabel><h3>{tr('pages.EmailCompose.add', { typedEmail })}</h3></IonLabel>
            </IonItem>
          )}
          {filtered.length === 0 && !isValidEmail ? (
            <IonLabel className={styles.searchEmpty}>{tr('pages.EmailCompose.noMatches')}</IonLabel>
          ) : filtered.map(o => (
            <IonItem key={o.key} button detail={false} lines="none"
              className={`${styles.dropdownItem}${emails.includes(o.email) ? ` ${styles.dropdownItemActive}` : ''}`}
              onClick={() => addEmail(o.email)}>
              <IonLabel>
                <h3>{o.name}</h3>
                <p className={styles.dropdownItemTip}>{o.email}</p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </PickerDropdown>
      {emails.length > 0 && (
        <IonLabel className={styles.chips}>
          {emails.map(email => (
            <IonLabel key={email} className={styles.chip}>
              <span>{email}</span>
              <IonButton fill="clear" className={styles.chipRemove} onClick={() => removeEmail(email)}>
                <IonIcon icon={closeOutline} slot="icon-only" />
              </IonButton>
            </IonLabel>
          ))}
        </IonLabel>
      )}
    </IonLabel>
  );
}

// Zoho's default Sales Order email template (unlike its Invoice one, which
// renders correctly at its natural height) uses a table layout with a large
// fixed-height trailing spacer — document.documentElement.scrollHeight
// faithfully reports that, but rendering it 1:1 turns the SO preview into
// thousands of px of blank white below the actual message. Capped for
// salesorder only, below, so the (already correct) invoice preview is untouched.
const MAX_PREVIEW_HEIGHT = 1400;

interface EmailComposeProps {
  entityType: ZohoEmailEntityType;
  entityId: string;
  // Shown in the header subtitle, e.g. "SO-000123" — purely informational.
  entityNumber?: string;
  // Falls back into "To" when Zoho's email-content response has no recipient
  // on file for this contact — e.g. the invoice/SO's own customer email.
  defaultTo?: string;
  onClose?: () => void;
  onSent?: () => void;
}

const EmailCompose: React.FC<EmailComposeProps> = ({ entityType, entityId, entityNumber, defaultTo, onClose, onSent }) => {
  const { t: tr } = useTranslation();
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const templateFieldRef = useRef<HTMLIonItemElement>(null);
  const previewFrameRef = useRef<HTMLIFrameElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [templates, setTemplates] = useState<ZohoEmailTemplateOption[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [templateOpen, setTemplateOpen] = useState(false);

  const [fromOptions, setFromOptions] = useState<ZohoEmailFromAddress[]>([]);
  const [fromKey, setFromKey] = useState('');

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachStatement, setAttachStatement] = useState(false);
  const [attachPdf, setAttachPdf] = useState(true);
  const [previewHeight, setPreviewHeight] = useState(400);
  const [previewUrl, setPreviewUrl] = useState('');
  // Contacts Zoho itself associates with THIS invoice/order (to_contacts/
  // cc_mails_list) — the To/Cc pickers offer only these, not every contact in
  // the whole company directory.
  const [toContacts, setToContacts] = useState<ZohoEmailContact[]>([]);
  const [ccContacts, setCcContacts] = useState<ZohoEmailContact[]>([]);

  const closeTemplatePicker = useCallback(() => setTemplateOpen(false), []);

  // The iframe is sandboxed with allow-scripts but deliberately WITHOUT
  // allow-same-origin — granting both together is what triggers the browser's
  // "can escape its sandboxing" warning, since same-origin access would let
  // any script in Zoho's template reach into this app's own DOM. Without
  // allow-same-origin, contentDocument access from the parent is blocked, so
  // auto-height can't read it directly — instead, a small measurement script
  // (mine, not Zoho's) is appended to the preview HTML and reports its
  // height back via postMessage, which works cross-origin by design.
  // Clearing the preview URL when there's no body is computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as a synchronous setState call at the top of the effect,
  // which now only handles the actual Blob URL creation/cleanup.
  const [prevBodyForPreview, setPrevBodyForPreview] = useState(body);
  if (body !== prevBodyForPreview) {
    setPrevBodyForPreview(body);
    if (!body) setPreviewUrl('');
  }
  useEffect(() => {
    if (!body) return;
    const measureScript = `<script>(function(){function r(){parent.postMessage({source:'rsmaids-email-preview',height:document.documentElement.scrollHeight||document.body.scrollHeight},'*');}if(document.readyState==='complete'){r();}else{window.addEventListener('load',r);}})();</script>`;
    // Blob(...) encodes the string to UTF-8 bytes, but without a charset in
    // the MIME type the HTML parser doesn't know that when decoding them back
    // — it falls back to a different default and mangles non-ASCII characters
    // (arrows, smart quotes, em-dashes) into mojibake like "â†'".
    const blob = new Blob([stripScripts(body) + measureScript], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — the cleanup below still correctly revokes this
    // same `url` regardless of that timing.
    Promise.resolve().then(() => setPreviewUrl(url));
    return () => URL.revokeObjectURL(url);
  }, [body]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      // Only trust messages that actually came from this specific iframe's
      // own window — the shape check alone doesn't rule out some other
      // script on the page sending a look-alike message.
      if (e.source !== previewFrameRef.current?.contentWindow) return;
      if (e.data?.source === 'rsmaids-email-preview' && typeof e.data.height === 'number') {
        const capped = entityType === 'salesorder' ? Math.min(e.data.height, MAX_PREVIEW_HEIGHT) : e.data.height;
        setPreviewHeight(capped);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [entityType]);

  // Initial load — Zoho bundles recipients, from-addresses, and available
  // templates all in this one response (see ZohoEmailContent's field comment).
  // Resetting loading/error for the new fetch target is computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as synchronous setState calls at the top of the effect,
  // which now only handles the actual async fetch.
  const [prevEmailContentTarget, setPrevEmailContentTarget] = useState({ entityType, entityId });
  if (prevEmailContentTarget.entityType !== entityType || prevEmailContentTarget.entityId !== entityId) {
    setPrevEmailContentTarget({ entityType, entityId });
    setLoading(true);
    setError(null);
  }
  useEffect(() => {
    let cancelled = false;
    getEmailContent(entityType, entityId)
      .then(content => {
        if (cancelled) return;
        const toContactsList = content.to_contacts ?? [];
        const ccContactsList = content.cc_mails_list ?? [];
        setToContacts(toContactsList);
        setCcContacts(ccContactsList);
        const toEmails = toContactsList.filter(c => c.selected !== false && c.email).map(c => c.email as string);
        const ccEmails = ccContactsList.filter(c => c.selected !== false && c.email).map(c => c.email as string);
        // Mirrors the from-address fallback below (selected, else the first option) —
        // some modules (Sales Orders) return recipients with none marked `selected`,
        // where invoices' response always has one, so this path only shows there.
        const firstContactEmail = toContactsList.find(c => c.email)?.email ?? undefined;
        setTo(toEmails.length ? toEmails.join(', ') : (firstContactEmail ?? defaultTo ?? ''));
        setCc(ccEmails.join(', '));
        setBcc((content.bcc_mails ?? []).filter(Boolean).join(', '));
        setSubject(content.subject ?? '');
        setBody(content.body ?? '');
        setAttachPdf(content.attach_pdf ?? true);

        const tmpls = content.emailtemplates ?? [];
        setTemplates(tmpls);
        const selectedTemplate = tmpls.find(t => t.selected) ?? tmpls[0];
        setTemplateId(selectedTemplate?.email_template_id ?? '');

        const froms = content.from_emails ?? [];
        setFromOptions(froms);
        const selectedFrom = froms.find(f => f.selected) ?? froms[0];
        setFromKey(selectedFrom ? fromKeyOf(selectedFrom) : '');
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType, entityId, defaultTo]);

  // Switching templates only refreshes subject/body — recipients the user has
  // already typed/edited stay put, matching Zoho's own template-switch behavior.
  const handleTemplateChange = (id: string) => {
    setTemplateId(id);
    setTemplateOpen(false);
    setLoading(true);
    getEmailContent(entityType, entityId, id || undefined)
      .then(content => {
        setSubject(content.subject ?? '');
        setBody(content.body ?? '');
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  const parseEmails = (raw: string) =>
    raw.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

  const handleSend = async () => {
    const toMailIds = parseEmails(to);
    if (!toMailIds.length) { setSendError(tr('pages.EmailCompose.addAtLeastOneRecipient')); return; }
    setSendError(null);
    setSending(true);
    try {
      const selectedFrom = fromOptions.find(f => fromKeyOf(f) === fromKey);
      await sendEntityEmail(entityType, entityId, {
        toMailIds,
        ccMailIds: parseEmails(cc),
        bccMailIds: parseEmails(bcc),
        subject,
        body,
        sendCustomerStatement: attachStatement,
        sendAttachment: attachPdf,
        ...(selectedFrom?.is_org_email_id ? { sendFromOrgEmailId: true } : {}),
        ...(!selectedFrom?.is_org_email_id && selectedFrom?.organization_contact_id
          ? { fromAddressId: selectedFrom.organization_contact_id } : {}),
      });
      onSent?.();
      onClose?.();
    } catch (err) {
      setSendError(tr('pages.EmailCompose.failedToSend', { v1: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSending(false);
    }
  };

  const selectedTemplateName = templates.find(t => t.email_template_id === templateId)?.name
    ?? (loading ? 'Loading…' : 'Default');

  const contactsToOptions = (contacts: ZohoEmailContact[]): RecipientOption[] =>
    contacts.filter(c => c.email).map((c, i) => ({
      key: String(c.contact_person_id ?? i),
      name: [c.first_name, c.last_name].filter(Boolean).join(' ') || (c.email as string),
      email: c.email as string,
    }));
  const toOptions = contactsToOptions(toContacts);
  const ccContactOptions = contactsToOptions(ccContacts);
  // Zoho only ever sends a curated contact list for To/Cc — Bcc has no
  // equivalent list of its own (just the already-picked bcc_mails strings),
  // and some modules (Sales Orders) leave Cc's own list empty too even when
  // the same contacts are available under To. Both fall back to the same
  // pool of known contacts so every field's dropdown offers real choices,
  // not an empty list.
  const allContactOptions = [...toOptions, ...ccContactOptions]
    .filter((option, index, arr) => arr.findIndex(o => o.email === option.email) === index);
  const ccOptions = ccContactOptions.length ? ccContactOptions : allContactOptions;
  const bccOptions = allContactOptions;

  const pageContent = (
    <>
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <BasePageWithHeader
        title={entityNumber ? tr('pages.EmailCompose.email2', { entityNumber }) : tr('pages.EmailCompose.email')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton fill="clear" className={styles.backButton} onClick={onClose}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        }
        endButtons={
          <IonButton fill="clear" className={styles.sendButton} onClick={handleSend} disabled={sending || loading}>
            {sending
              ? <IonSpinner name="bubbles" color="primary" style={{ width: 18, height: 18 }} />
              : <IonIcon icon={send} slot="icon-only" className={styles.sendIcon} />}
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={styles.mainContent}>
          <IonGrid className={styles.innerGrid}>
          {error && <IonLabel className={styles.errorBanner}>{error}</IonLabel>}
          {sendError && <IonLabel className={styles.errorBanner}>{sendError}</IonLabel>}

          <IonCard className={styles.card}>
            <IonCardContent className={styles.cardBody}>
              <IonLabel className={styles.cardTitle}>
                <IonIcon icon={mailOutline} />{tr('pages.EmailCompose.message')}
              </IonLabel>
              <IonLabel className={styles.formGrid}>
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.EmailCompose.from')}</span>
                  <FromField options={fromOptions} selectedKey={fromKey} onChange={setFromKey} portalRef={pickerPortalRef} />
                </IonLabel>
                <RecipientField label={tr('pages.EmailCompose.to')} value={to} onChange={setTo} portalRef={pickerPortalRef} options={toOptions} />
                <RecipientField label={tr('pages.EmailCompose.cc')} value={cc} onChange={setCc} portalRef={pickerPortalRef} options={ccOptions} />
                <RecipientField label={tr('pages.EmailCompose.bcc')} value={bcc} onChange={setBcc} portalRef={pickerPortalRef} options={bccOptions} />
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.EmailCompose.subject')}</span>
                  <input className={styles.input} value={subject} onChange={e => setSubject(e.target.value)} />
                </IonLabel>
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('pages.EmailCompose.template')}</span>
                  <IonItem ref={templateFieldRef} button detail={false} lines="none" className={styles.selectField}
                    onClick={() => setTemplateOpen(v => !v)}>
                    <IonLabel>{selectedTemplateName}</IonLabel>
                    <IonIcon icon={chevronDownOutline} slot="end"
                      className={`${styles.selectCaret} ${templateOpen ? styles.selectCaretOpen : ''}`} />
                  </IonItem>
                </IonLabel>
              </IonLabel>
            </IonCardContent>
          </IonCard>

          <PickerDropdown isOpen={templateOpen} fieldRef={templateFieldRef} portalRef={pickerPortalRef}
            close={closeTemplatePicker} className={styles.calendarDropdown}>
            <IonList lines="none" className={styles.dropdownList}>
              {templates.length === 0 ? (
                <IonLabel className={styles.searchEmpty}>{tr('pages.EmailCompose.noTemplates')}</IonLabel>
              ) : templates.map(t => (
                <IonItem key={t.email_template_id} button detail={false} lines="none"
                  className={`${styles.dropdownItem}${templateId === t.email_template_id ? ` ${styles.dropdownItemActive}` : ''}`}
                  onClick={() => handleTemplateChange(t.email_template_id)}>
                  <IonLabel>{t.name}</IonLabel>
                </IonItem>
              ))}
            </IonList>
          </PickerDropdown>

          <IonCard className={`${styles.card} ${styles.previewCardOuter}`}>
            <IonCardContent className={styles.previewCardBody}>
              <IonLabel className={`${styles.cardTitle} ${styles.previewCardTitle}`}>
                <IonIcon icon={documentTextOutline} />{tr('pages.EmailCompose.preview')}
              </IonLabel>
              {loading ? (
                <IonLabel className={styles.previewLoading}>
                  <IonSpinner name="bubbles" color="primary" />{tr('pages.EmailCompose.loadingEmailPreview')}
                </IonLabel>
              ) : (
                <IonCard className={styles.previewCard}>
                  <iframe
                    ref={previewFrameRef}
                    className={styles.previewFrame}
                    style={{ height: `${previewHeight}px` }}
                    src={previewUrl || undefined}
                    // allow-scripts only — no allow-same-origin (see the postMessage
                    // effect above for why: that combination is what triggers the
                    // "can escape its sandboxing" warning).
                    sandbox="allow-scripts"
                    title={tr('pages.EmailCompose.emailPreview')}
                  />
                </IonCard>
              )}
            </IonCardContent>
          </IonCard>

          <IonCard className={styles.card}>
            <IonCardContent className={styles.cardBody}>
              <IonLabel className={styles.cardTitle}>
                <IonIcon icon={documentTextOutline} />{tr('pages.EmailCompose.attachments')}
              </IonLabel>
              <IonItem lines="none" className={styles.checkboxRow}>
                <IonCheckbox slot="start" checked={attachStatement}
                  onIonChange={e => setAttachStatement(e.detail.checked)} />
                <IonLabel>{tr('pages.EmailCompose.attachCustomerStatement')}</IonLabel>
              </IonItem>
              <IonItem lines="none" className={styles.checkboxRow}>
                <IonCheckbox slot="start" checked={attachPdf}
                  onIonChange={e => setAttachPdf(e.detail.checked)} />
                <IonLabel>{tr('pages.EmailCompose.attach')} {entityType === 'salesorder' ? tr('pages.EmailCompose.serviceOrder') : tr('pages.EmailCompose.invoice')}{tr('pages.EmailCompose.pdf')}</IonLabel>
              </IonItem>
              {attachPdf && entityNumber && (
                <IonItem lines="none" className={styles.attachmentRow}>
                  <IonIcon icon={documentTextOutline} slot="start" className={styles.attachmentIcon} />
                  <IonLabel>{entityNumber}</IonLabel>
                </IonItem>
              )}
            </IonCardContent>
          </IonCard>
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>
    </>
  );

  return onClose ? <IonLabel className={styles.modalWrapper}>{pageContent}</IonLabel> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default EmailCompose;
